/* Adaptive submission form helpers: detail modes + capture score. */

export const MODES = [
  { key: "2min", label: "2 Minutes", desc: "Capture the essentials quickly." },
  { key: "5min", label: "5 Minutes", desc: "Add enough detail for a strong project record." },
  { key: "full", label: "Full Detail", desc: "Complete the full technical project submission." },
];

export const modeRank = (m) => ({ "2min": 1, "5min": 2, full: 3 }[m] || 3);

export const VEHICLE_OPTIONS = ["IPV", "3.5 Dropside TM", "Welfare Vans", "TM Vans"];

export const FACT_CARDS = [
  { key: "project_value", label: "Project value", ph: "e.g. £450,000", modes: 1 },
  { key: "duration", label: "Duration", ph: "e.g. 12 weeks", modes: 1 },
  { key: "useful_numbers", label: "Other useful number", ph: "e.g. 2 miles of closures", modes: 1 },
  { key: "length_of_works", label: "Length of works", ph: "e.g. 2 miles of TM installed", modes: 2 },
  { key: "materials_summary", label: "Equipment used", ph: "e.g. 400 cones, 60 signs", modes: 2 },
  { key: "assets_count", label: "Assets installed or deployed", ph: "e.g. 12 VMS units", modes: 2 },
  { key: "operatives", label: "Number of operatives", ph: "e.g. 18", modes: 2 },
  { key: "crews", label: "Number of crews", ph: "e.g. 4 crews", modes: 2 },
  { key: "plant_used", label: "Plant used", ph: "e.g. IPV, crash cushion, crane", modes: 3 },
  { key: "traffic_management", label: "Traffic management", ph: "e.g. Night-time contraflow", modes: 3 },
  { key: "environmental_measures", label: "Environmental measures", ph: "e.g. Euro 6 fleet, reduced idling", modes: 3 },
  { key: "awards", label: "Awards or recognition", ph: "e.g. Considerate Constructors 45/50", modes: 3 },
];

const FACT_KEYS = FACT_CARDS.map((f) => f.key);

export function computeCaptureScore(project) {
  const has = (k) => !!(project?.[k] && String(project[k]).trim());
  const files = (project?.files || []).filter((f) => !f.is_deleted);
  const images = files.filter((f) => (f.content_type || "").startsWith("image/"));
  const imgMatch = (names) =>
    images.some(
      (f) =>
        names.some((n) => (f.category || "").toLowerCase().includes(n)) ||
        (f.tags || []).some((t) => names.some((n) => t.toLowerCase().includes(n)))
    );
  const factCount =
    FACT_KEYS.filter(has).length +
    (project?.custom_facts || []).filter((f) => f.value && String(f.value).trim()).length;

  let score = 0;
  const add = (cond, pts) => cond && (score += pts);
  add(has("project_name"), 3);
  add(has("client_name"), 3);
  add(has("location"), 3);
  add(has("project_status"), 2);
  add((project?.categories || []).length > 0, 3);
  add((project?.services_delivered || []).length > 0, 3);
  add(has("one_sentence"), 12);
  add(has("why_needed"), 4);
  add((project?.challenges || []).length > 0, 3);
  add(has("main_challenge_text"), 10);
  add((project?.solutions || []).length > 0, 3);
  add(has("works_carried_out"), 10);
  add(has("how_delivered"), 4);
  add((project?.outcomes || []).length > 0, 3);
  add(has("main_result"), 10);
  add(has("client_public_benefit"), 6);
  add(has("client_feedback"), 6);
  add(factCount >= 3, 6);
  add(images.length > 0, 4);
  add(imgMatch(["before"]), 3);
  add(imgMatch(["completed", "after"]), 3);
  score = Math.min(100, score);

  let status = "Basic Record";
  if (score >= 85) status = "Complete Submission";
  else if (score >= 65) status = "Strong Submission";
  else if (score >= 40) status = "Good Submission";

  const suggestions = [];
  if (!has("one_sentence")) suggestions.push("Add the short project summary");
  if (!has("main_result")) suggestions.push("Add the main project outcome");
  if (!imgMatch(["completed", "after"])) suggestions.push("Add a completed project photo");
  if (factCount < 3) suggestions.push("Add one useful project figure");
  if (!has("client_feedback")) suggestions.push("Add client feedback");
  if (!has("main_challenge_text")) suggestions.push("Add the main challenge");

  return { score, status, suggestions: suggestions.slice(0, 3) };
}
